import type { MenuItem } from '@/types';

/**
 * Cheapest reachable price for a product whose price lives in its options.
 *
 * The platform's convention for size/quantity variants is `price = 0` plus a
 * REQUIRED option group whose `priceDelta` carries the full price (3 pcs 4,00 €
 * / 6 pcs 7,00 €, verre / 37,5 cl / 75 cl, …). One product, one cart line, price
 * resolved by the choice. The catalogue card, however, printed the raw `price` —
 * so a category opened on "0,00 €", i.e. announced a free dish.
 *
 * Returns the sum of the cheapest option of every required group, or `null`
 * when the product does not follow that convention (no required group, or its
 * options are plain free supplements) and the plain price should be shown.
 *
 * Reads `item.optionGroups` — the PER-PRODUCT resolved groups (SC-93), so the
 * product's own price overrides are already applied.
 */
export function fromPriceCents(item: MenuItem): number | null {
  const required = (item.optionGroups ?? []).filter(
    (g) => g.required && g.options.length > 0
  );
  if (required.length === 0) return null;

  const total = required.reduce(
    (sum, g) => sum + Math.min(...g.options.map((o) => o.priceDelta)),
    0
  );
  return total > 0 ? total : null;
}
